import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  ListItemText
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import CustomPagination from '../../../../components/common/CustomPagination';
import type { InvoiceFilterValues } from '../../../../components/Billing/Invoice/InvoiceFilter';
import style from '../Advance.module.css';
import { requestRefundItem,getRecievedRequestPatientList, ReceivedAdvanceListResponse, AdvanceReceiptData, getAdvanceReceiptURL } from '../advance.helper';
import TableSkeleton from '../../../../components/common/TableSkeleton';
import { useAppQuery } from '../../../../services/appQuery';
import { getRequest } from '../../../../services/httpService';
import { convertDateToDDMMYYYY, storePrevRoute } from '../../../../utils/commonUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as PrintIcon } from '../../../../assets/SVG/printerblue.svg';
import { useClinic } from '../../../../components/ClinicProvider';
import ReusableReportComponent from '../../../../components/printReports/ReusableReportComponent';
import ReceiptComponent from '../ReceiptComponent';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../../components/alertProvider';
import { useTranslation } from 'react-i18next';
import TruncatedText from '../../../../utils/TruncatedText';

export interface ReceivedListProps {
  search: string;
  filters: InvoiceFilterValues;
  patientId?: number;
}

const formatMoney = (val: number) => val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ReceivedList: React.FC<ReceivedListProps> = ({ search, filters, patientId }) => {
  const [page, setPage] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [selectedRow, setSelectedRow] = useState<requestRefundItem | null>(null);
  const navigate = useNavigate();
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const [receiptData, setReceiptData] = useState<AdvanceReceiptData | null>(null);
  const { addAlert } = useAlert();
  const location = useLocation();
  const { t : tBilling } = useTranslation('billing_config');
  const [pageSize, setPageSize] = useState(10);
  const { ClinicId } = useClinic();

  
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

    
  const { data: ReceivedAdvanceRequests, isLoading} = useAppQuery<ReceivedAdvanceListResponse>(
    [
      'ReceivedAdvanceList',
      page,
      pageSize,
      search,
      filters.fromDate,
      filters.toDate,
      patientId
    ],
    () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      if (search) params.set('search', search);
      if (filters.fromDate)
        params.set('checked_in_date_after', filters.fromDate);
      if (filters.toDate)
        params.set('checked_in_date_before', filters.toDate);
      if (patientId) params.set('patient_id', String(patientId));
      return getRequest<ReceivedAdvanceListResponse>(`${getRecievedRequestPatientList()}?${params.toString()}`);
    },
    { 
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true
    }
  );

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, row: requestRefundItem) => {
    setMenuAnchor(e.currentTarget);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  
  const requestRefund = (row: requestRefundItem) => {
    if(!patientId){
      storePrevRoute(location.pathname, location.search);
    }
    navigate(`/admin/billing/advance/request-refund/${row.patient_id}`, { state: { summary: row } });
    handleMenuClose();
  };

  const { mutate: fetchReceipt, isPending: isPrinting } = useMutation({
    mutationFn: (creditId: number): Promise<AdvanceReceiptData> => getRequest(getAdvanceReceiptURL(creditId)),
    onSuccess: (data:AdvanceReceiptData) => {
      setReceiptData(data);
      setShowPrint(true);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('Advance.messages.failed_to_load_receipt', { defaultValue: 'Failed to load receipt.' });
      addAlert(message, 'error');
    },
  });

  const handleClose = () => { setShowPrint(false); };

  return (
    <Box data-testid="received-list-container">
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="received-list-table-container">
        <Table aria-label={tBilling('Advance.aria.received_list_table', { defaultValue: 'Received list' })} data-testid="received-list-table">
          <TableHead>
            <TableRow sx={{bgcolor:'#FAFAFA'}}>
              {!patientId && (
                <TableCell className={style.tableHeader}>{tBilling('Advance.patient')}</TableCell>
              )}
              <TableCell className={style.tableHeader}>{tBilling('Advance.receipt_no')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.clinic')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.date')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.total_amt')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.used_amt')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.refund_amt')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.balance_amt')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.remark')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.against')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.package_name')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.package_bill_freeze')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableSkeleton
                columnWidths={patientId
                  ? [200, 160, 140, 120, 120, 120, 120, 160, 200, 240, 120, 140]
                  : [260, 200, 160, 140, 120, 120, 120, 120, 160, 200, 240, 120, 140]
                }
                rows={pageSize}
              />
            ) : (
              <>
                {ReceivedAdvanceRequests?.objects.map((row, idx) => (
                  <TableRow key={idx} hover>
                    {!patientId && (
                      <TableCell className={style.tableCells} sx={{ minWidth: 260 }}>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" className={style.tableCells} sx={{ fontWeight: 600 }}>
                            {row?.patient_name} | {row?.age}
                          </Typography>
                          <Typography variant="caption" className={style.tableCells} color="text.secondary">
                            {row?.medical_record_no} | {row?.gender}
                          </Typography>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell className={style.tableCells} sx={{ minWidth: 200 }}>{row?.receipt_no ?? '-'}</TableCell>
                    <TableCell className={style.tableCells} sx={{ minWidth: 160 }}>{row?.clinic}</TableCell>
                    <TableCell className={style.tableCells} sx={{ minWidth: 140 }}>{row?.transaction_date ? convertDateToDDMMYYYY(row.transaction_date) : '-'}</TableCell>
                    <TableCell className={style.tableCells} >{formatMoney(row?.total_amount)}</TableCell>
                    <TableCell className={style.tableCells} >{formatMoney(row?.used_amount)}</TableCell>
                    <TableCell className={style.tableCells} >{formatMoney(row?.refund_amount)}</TableCell>
                    <TableCell className={style.tableCells} >{formatMoney(row?.balance_amount)}</TableCell>
                    <TableCell className={style.tableCells} sx={{ minWidth: 160, maxWidth: 300 }}>
                      {row?.remark ? <TruncatedText text={row.remark} title={tBilling('Advance.remark')} iconPosition="left" /> : '-'}
                    </TableCell>
                    <TableCell className={style.tableCells} sx={{ minWidth: 200 }}>{row?.advance_against ?? '-'}</TableCell>
                    <TableCell className={style.tableCells} sx={{ minWidth: 240 }}>{row?.package_name ?? '-'}</TableCell>
                    <TableCell align="center" className={style.tableCells} sx={{ width: 120 }}>
                      <Checkbox
                        disabled
                        checked={row?.package_bill_freeze}
                        size="small"
                        sx={{
                          color: 'success.main',
                          '&.Mui-checked': { color: 'success.main' }
                        }}
                      />
                    </TableCell>
                    <TableCell  className={style.tableCells} sx={{ width: 140 }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton size="small" aria-label={tBilling('Advance.aria.print_receipt', { defaultValue: 'Print receipt' })} onClick={() => fetchReceipt(row.credit_transaction_id)} disabled={isPrinting}> 
                          <PrintIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={tBilling('Advance.aria.more_actions', { defaultValue: 'More actions' })}
                          onClick={(e) => handleMenuOpen(e, row)}
                          sx={{
                            visibility: row?.balance_amount > 0 && !row?.package_bill_freeze && ClinicId && row?.clinic_id != null && Number(row.clinic_id) === Number(ClinicId) ? 'visible' : 'hidden'
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {ReceivedAdvanceRequests?.objects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={patientId ? 12 : 13}>
                      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        {tBilling('Advance.no_entries_found')}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <MenuItem
          onClick={() => {
            if (selectedRow) {
              requestRefund(selectedRow);
            }
          }}
        >
          <ListItemText primary={tBilling('Advance.refund')} />
        </MenuItem>
      </Menu>

      <CustomPagination
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 15, 25, 50]}
        onPageSizeChange={handlePageSizeChange}
        totalCount={ReceivedAdvanceRequests?.meta.total_count ?? 0}
        onPageChange={(_, newPage) => setPage(newPage)}
      />

      <ReusableReportComponent
        open={showPrint}
        onClose={handleClose}
        reportTitle={tBilling('Advance.advance_receipt', { defaultValue: 'Advance Receipt' })}
      >
        {receiptData ? (
          <ReceiptComponent data={receiptData} />
        ) : (
          <Typography variant="h6" sx={{ mb: 2 }}>{tBilling('Advance.messages.no_receipt_data_available', { defaultValue: 'No receipt data available.' })}</Typography>
        )}
      </ReusableReportComponent>
    </Box>
  );
};

export default ReceivedList;
